import dayjs from "dayjs";

const TASK_PATTERN = /(?:^|\s)\/task(?=\s|$|\/due|\/task)/i;
const DUE_PATTERN = /(?:^|\s)\/due(?:\s+([^\s/][^\n]*))?(?=\s|$|\/due|\/task)/i;
const DATE_TAG_PATTERN =
  /(?:^|[\s,;:])\/(next[ \t]+week|today|tomorrow|monday|mon|tuesday|tue|tues|wednesday|wed|thursday|thu|thur|thurs|friday|fri|saturday|sat|sunday|sun)(?=\s|$|\/task)/i;

export const DUE_SHORTCUTS = [
  { key: "today", label: "Today" },
  { key: "tomorrow", label: "Tomorrow" },
  { key: "friday", label: "Friday" },
  { key: "next week", label: "Next week" },
];

const WEEKDAYS = {
  monday: 0,
  mon: 0,
  tuesday: 1,
  tue: 1,
  tues: 1,
  wednesday: 2,
  wed: 2,
  thursday: 3,
  thu: 3,
  thur: 3,
  thurs: 3,
  friday: 4,
  fri: 4,
  saturday: 5,
  sat: 5,
  sunday: 6,
  sun: 6,
};

function collapseWhitespace(text) {
  return text.replace(/\s+/g, " ").trim();
}

function mondayIndex(date) {
  return (date.getDay() + 6) % 7;
}

function nextWeekday(fromDay, weekday) {
  const daysAhead = (weekday - mondayIndex(fromDay) + 7) % 7;
  const result = new Date(fromDay);
  result.setDate(result.getDate() + daysAhead);
  return result;
}

export function toDayjs(value) {
  if (!value) return null;
  const parsed = dayjs.isDayjs(value) ? value : dayjs(value);
  return parsed.isValid() ? parsed : null;
}

export function resolveDuePhrase(phrase, anchorDate = new Date()) {
  if (!phrase) return null;
  const cleaned = phrase.trim().replace(/[,.]$/, "");
  if (!cleaned) return null;
  const lower = cleaned.toLowerCase();
  const today = new Date(anchorDate);
  today.setHours(0, 0, 0, 0);
  if (lower === "today") return today;
  if (lower === "tomorrow") {
    const d = new Date(today);
    d.setDate(d.getDate() + 1);
    return d;
  }
  if (/^next[ \t]+week$/.test(lower)) {
    const d = new Date(today);
    d.setDate(d.getDate() + 7 - mondayIndex(today));
    return d;
  }
  if (lower in WEEKDAYS) {
    return nextWeekday(today, WEEKDAYS[lower]);
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
    const [y, m, d] = cleaned.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  const slash = cleaned.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    return new Date(Number(slash[3]), Number(slash[2]) - 1, Number(slash[1]));
  }
  return null;
}

export function activeDueShortcut(text, cursor) {
  const match = /(^|\s)\/([^\n@]{0,20})$/.exec(text.slice(0, cursor));
  if (!match) return null;
  const typed = match[2].toLowerCase().trim();
  if (typed === "task" || typed.startsWith("task ") || typed.startsWith("due")) return null;
  const options = DUE_SHORTCUTS.filter(
    item => !typed || item.key.startsWith(typed) || typed.startsWith(item.key.split(" ")[0]),
  );
  return {
    start: match.index + match[1].length,
    end: cursor,
    typed,
    options,
  };
}

export function formatDueDate(value, { includeTime = false } = {}) {
  const parsed = toDayjs(value);
  if (!parsed) return "";
  if (includeTime && (parsed.hour() !== 0 || parsed.minute() !== 0)) {
    return parsed.format("ddd D MMM YYYY, h:mm a");
  }
  return parsed.format("ddd D MMM YYYY");
}

export function toIsoDate(value) {
  const parsed = toDayjs(value);
  return parsed ? parsed.format("YYYY-MM-DD") : null;
}

export function parseNoteCommands(content, { allowTaskCommands = true, pickerDueDate = null } = {}) {
  const raw = content || "";
  const hasTaskToken = allowTaskCommands && TASK_PATTERN.test(raw);
  const dueMatch = allowTaskCommands ? DUE_PATTERN.exec(raw) : null;
  const dateTagMatch = allowTaskCommands ? DATE_TAG_PATTERN.exec(raw) : null;
  const duePhrase = dueMatch?.[1]?.replace(TASK_PATTERN, " ").trim() || "";
  const hasDueToken = Boolean(dueMatch || dateTagMatch);

  let dueDate = toDayjs(pickerDueDate)?.toDate() || null;
  if (!dueDate && hasDueToken) {
    dueDate = resolveDuePhrase(duePhrase || dateTagMatch?.[1] || "today");
  }

  const isTask = allowTaskCommands && (hasTaskToken || hasDueToken || Boolean(dueDate));

  let cleaned = raw;
  if (dueMatch) {
    cleaned = `${cleaned.slice(0, dueMatch.index)}${cleaned.slice(dueMatch.index + dueMatch[0].length)}`;
  }
  cleaned = cleaned.replace(DATE_TAG_PATTERN, " ");
  cleaned = cleaned.replace(TASK_PATTERN, " ");
  cleaned = collapseWhitespace(cleaned);

  return {
    content: cleaned,
    isTask,
    dueDate,
    hasTaskToken,
    hasDueToken,
  };
}
